import { CommonModule, isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import {
  FlyerPopupCloseEvent,
  FlyerPopupCloseReason,
  FlyerPopupImageState,
  FlyerPopupItem,
  FlyerPopupSlideChangeEvent,
  FlyerPopupSlideSource,
} from './flyer-popup.models';
import { FlyerPopupService } from './flyer-popup.service';
import { NotifyService } from '@shared/services/notify/notify.service';
import { handleHttpError } from '@shared/utils/handle-http-error.util';

@Component({
  selector: 'app-flyer-popup',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './flyer-popup.component.html',
  styleUrls: ['./flyer-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlyerPopupComponent implements OnInit, OnDestroy {
  @ViewChild('popupDialog') private readonly popupDialog?: ElementRef<HTMLDialogElement>;

  @Input() autoOpen = true;
  @Input() autoplay = false;
  @Input() autoplayIntervalMs = 8000;
  @Input() closeOnEscape = true;
  @Input() pauseAutoplayOnHover = true;
  @Input() pauseAutoplayOnFocus = true;
  @Input() showBlurBackground = true;
  @Input() minSwipeDistancePx = 48;
  @Input() defaultImageUrl = '';

  @Output() closed = new EventEmitter<FlyerPopupCloseEvent>();
  @Output() loadCompleted = new EventEmitter<boolean>();
  @Output() slideChange = new EventEmitter<FlyerPopupSlideChangeEvent>();

  flyers: FlyerPopupItem[] = [];
  activeIndex = 0;

  showPopup = false;
  isClosing = false;
  isSlideChanging = false;
  imageState: FlyerPopupImageState = 'idle';
  displayImageUrl: string | null = null;

  private readonly isBrowser: boolean;
  private readonly flyerPopupService = inject(FlyerPopupService);
  private readonly notifyService = inject(NotifyService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly closeAnimationMs = 280;
  private readonly slideTransitionMs = 160;
  private readonly imagePreloadTimeoutMs = 5000;

  private readonly destroy$ = new Subject<void>();

  private autoplayTimer: ReturnType<typeof globalThis.setTimeout> | null = null;
  private closeTimeout: ReturnType<typeof globalThis.setTimeout> | null = null;
  private slideTimeout: ReturnType<typeof globalThis.setTimeout> | null = null;
  private loadRequestId = 0;
  private slideRequestId = 0;
  private isLoadingFlyers = false;

  private readonly preloadedImageUrls = new Set<string>();
  private autoplayHoverPaused = false;
  private autoplayFocusPaused = false;

  private pointerActive = false;
  private pointerStartX = 0;
  private documentScrollLocked = false;
  private previousBodyOverflow = '';
  private previousBodyPaddingRight = '';
  private previousDocumentOverflow = '';

  constructor() {
    this.isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  }

  ngOnInit(): void {
    if (this.autoOpen) {
      this.loadFlyers();
    }
  }

  ngOnDestroy(): void {
    this.stopAutoplay();
    this.loadRequestId += 1;
    this.slideRequestId += 1;
    this.clearCloseTimeout();
    this.clearSlideTimeout();
    this.unlockDocumentScroll();

    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasFlyers(): boolean {
    return this.flyers.length > 0;
  }

  get hasMultipleFlyers(): boolean {
    return this.flyers.length > 1;
  }

  get currentFlyer(): FlyerPopupItem | null {
    if (!this.hasFlyers) return null;

    return this.flyers[this.activeIndex] ?? null;
  }

  get isImageLoading(): boolean {
    return this.imageState === 'loading';
  }

  get isImageError(): boolean {
    return this.imageState === 'error';
  }

  get backgroundImageStyle(): string {
    if (!this.displayImageUrl) return 'none';

    return `url(${JSON.stringify(this.displayImageUrl)})`;
  }

  get showAutoplayProgress(): boolean {
    return this.autoplay && this.hasMultipleFlyers;
  }

  get showActiveAutoplayProgress(): boolean {
    return this.showAutoplayProgress && !this.isSlideChanging && !this.isClosing;
  }

  get autoplayProgressDurationMs(): number {
    return this.resolveAutoplayInterval();
  }

  get autoplayProgressAnimationState(): 'paused' | 'running' {
    if (!this.showAutoplayProgress || this.autoplayPaused || this.isSlideChanging || this.isClosing) {
      return 'paused';
    }

    return 'running';
  }

  get shouldReserveImageSpace(): boolean {
    return !this.displayImageUrl || this.isImageLoading || this.isSlideChanging;
  }

  loadFlyers(): void {
    if (this.isLoadingFlyers || this.showPopup || this.isClosing) {
      return;
    }

    const requestId = ++this.loadRequestId;
    this.isLoadingFlyers = true;

    this.flyerPopupService
      .getActiveBackOfficeFlyers()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (flyers) => {
          if (requestId !== this.loadRequestId) {
            return;
          }

          this.isLoadingFlyers = false;
          this.openWithFlyers(flyers);
          this.loadCompleted.emit(flyers.length > 0);
        },
        error: (error: HttpErrorResponse) => {
          if (requestId !== this.loadRequestId) {
            return;
          }

          this.isLoadingFlyers = false;
          this.handleFlyersLoadError(error);
          this.loadCompleted.emit(false);
        },
      });
  }

  private handleFlyersLoadError(error: HttpErrorResponse): void {
    this.flyers = [];
    this.showPopup = false;
    this.stopAutoplay();
    this.unlockDocumentScroll();

    const { notifyTitle, notifyMessage, notifyType } = handleHttpError(error);

    this.notifyService.show({
      title: notifyTitle,
      message: notifyMessage,
      type: notifyType,
    });

    this.cdr.markForCheck();
  }

  openWithFlyers(flyers: FlyerPopupItem[]): void {
    this.stopAutoplay();
    this.slideRequestId += 1;
    this.clearCloseTimeout();
    this.clearSlideTimeout();
    this.preloadedImageUrls.clear();

    this.flyers = flyers;
    this.activeIndex = 0;
    this.showPopup = false;
    this.isClosing = false;
    this.isSlideChanging = false;
    this.autoplayHoverPaused = false;
    this.autoplayFocusPaused = false;

    if (!this.flyers.length) {
      this.showPopup = false;
      this.syncImageState();
      this.unlockDocumentScroll();
      this.cdr.markForCheck();
      return;
    }

    const requestId = this.slideRequestId;
    const firstImageUrl = this.resolveImageUrl(this.flyers[0] ?? null);

    this.syncImageState();

    void this.preloadImage(firstImageUrl).then(() => {
      if (requestId !== this.slideRequestId || !this.hasFlyers) {
        return;
      }

      this.showPopup = true;
      this.lockDocumentScroll();
      this.syncImageState();
      this.emitSlideChange('initial');
      this.startAutoplay();
      this.openNativeDialog();
      this.cdr.markForCheck();
    });

    this.cdr.markForCheck();
  }

  close(reason: FlyerPopupCloseReason): void {
    if (!this.showPopup || this.isClosing) return;

    this.stopAutoplay();
    this.slideRequestId += 1;
    this.clearSlideTimeout();

    this.isClosing = true;
    this.isSlideChanging = false;

    this.clearCloseTimeout();

    this.closeTimeout = globalThis.setTimeout(() => {
      this.closeNativeDialog();
      this.showPopup = false;
      this.isClosing = false;
      this.closeTimeout = null;
      this.unlockDocumentScroll();
      this.closed.emit({ reason });
      this.cdr.markForCheck();
    }, this.closeAnimationMs);

    this.cdr.markForCheck();
  }

  goPrevious(source: FlyerPopupSlideSource = 'arrow'): void {
    if (!this.hasMultipleFlyers) return;

    const nextIndex =
      this.activeIndex <= 0 ? this.flyers.length - 1 : this.activeIndex - 1;

    this.goTo(nextIndex, source);
  }

  goNext(source: FlyerPopupSlideSource = 'arrow'): void {
    if (!this.hasMultipleFlyers) return;

    const nextIndex =
      this.activeIndex >= this.flyers.length - 1 ? 0 : this.activeIndex + 1;

    this.goTo(nextIndex, source);
  }

  goTo(index: number, source: FlyerPopupSlideSource = 'programmatic'): void {
    if (!this.hasFlyers || this.isSlideChanging) return;

    const nextIndex = this.normalizeIndex(index);

    if (nextIndex === this.activeIndex) return;

    if (source !== 'autoplay') {
      this.stopAutoplay();
    }

    this.clearSlideTimeout();

    const requestId = ++this.slideRequestId;
    const nextImageUrl = this.resolveImageUrl(this.flyers[nextIndex] ?? null);

    this.isSlideChanging = true;

    void Promise.all([
      this.waitForSlideTransition(),
      this.preloadImage(nextImageUrl),
    ]).then(() => {
      this.commitSlideChange(nextIndex, source, requestId);
    });

    this.cdr.markForCheck();
  }

  onIndicatorClick(index: number): void {
    this.goTo(index, 'indicator');
  }

  onImageLoad(): void {
    this.imageState = 'loaded';

    if (this.displayImageUrl) {
      this.preloadedImageUrls.add(this.displayImageUrl);
    }

    this.cdr.markForCheck();
  }

  onImageError(): void {
    const fallback = this.defaultImageUrl.trim();

    if (fallback && this.displayImageUrl !== fallback) {
      this.displayImageUrl = fallback;
      this.imageState = 'loading';
      this.cdr.markForCheck();
      return;
    }

    this.displayImageUrl = null;
    this.imageState = 'error';
    this.cdr.markForCheck();
  }

  onMouseEnter(): void {
    if (!this.pauseAutoplayOnHover) return;

    this.autoplayHoverPaused = true;
    this.stopAutoplay();
    this.cdr.markForCheck();
  }

  onMouseLeave(): void {
    if (!this.pauseAutoplayOnHover) return;

    this.autoplayHoverPaused = false;

    if (!this.autoplayPaused) {
      this.restartAutoplay();
    }

    this.cdr.markForCheck();
  }

  onFocusIn(): void {
    if (!this.pauseAutoplayOnFocus) return;

    this.autoplayFocusPaused = true;
    this.stopAutoplay();
    this.cdr.markForCheck();
  }

  onFocusOut(): void {
    if (!this.pauseAutoplayOnFocus) return;

    this.autoplayFocusPaused = false;

    if (!this.autoplayPaused) {
      this.restartAutoplay();
    }

    this.cdr.markForCheck();
  }

  onDialogCancel(event: Event): void {
    event.preventDefault();

    if (!this.closeOnEscape) {
      return;
    }

    this.close('escape');
  }

  onPointerDown(event: PointerEvent): void {
    if (!this.hasMultipleFlyers) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.pointerActive = true;
    this.pointerStartX = event.clientX;

    const target = event.currentTarget as HTMLElement | null;
    target?.setPointerCapture?.(event.pointerId);
  }

  onPointerUp(event: PointerEvent): void {
    if (!this.pointerActive) return;

    const deltaX = event.clientX - this.pointerStartX;

    this.pointerActive = false;

    const target = event.currentTarget as HTMLElement | null;
    target?.releasePointerCapture?.(event.pointerId);

    if (Math.abs(deltaX) < this.minSwipeDistancePx) return;

    if (deltaX > 0) {
      this.goPrevious('swipe');
      return;
    }

    this.goNext('swipe');
  }

  onPointerCancel(): void {
    this.pointerActive = false;
  }

  trackByFlyer = (_: number, flyer: FlyerPopupItem): number => flyer.id;

  @HostListener('document:keydown.arrowleft', ['$event'])
  onArrowLeft(event: KeyboardEvent): void {
    if (!this.showPopup || !this.hasMultipleFlyers) return;

    event.preventDefault();
    this.goPrevious('arrow');
  }

  @HostListener('document:keydown.arrowright', ['$event'])
  onArrowRight(event: KeyboardEvent): void {
    if (!this.showPopup || !this.hasMultipleFlyers) return;

    event.preventDefault();
    this.goNext('arrow');
  }

  private syncImageState(): void {
    this.displayImageUrl = this.resolveImageUrl(this.currentFlyer);

    if (!this.displayImageUrl) {
      this.imageState = 'error';
      return;
    }

    this.imageState = this.preloadedImageUrls.has(this.displayImageUrl) ? 'loaded' : 'loading';
  }

  private resolveImageUrl(flyer: FlyerPopupItem | null): string | null {
    const imageUrl = flyer?.imageUrl?.trim() || this.defaultImageUrl.trim();

    return imageUrl || null;
  }

  private emitSlideChange(source: FlyerPopupSlideSource): void {
    this.slideChange.emit({
      item: this.currentFlyer,
      index: this.activeIndex,
      source,
    });
  }

  private normalizeIndex(index: number): number {
    if (!this.flyers.length) return 0;

    const safeIndex = Number.isFinite(index) ? Math.floor(index) : 0;
    const total = this.flyers.length;

    return ((safeIndex % total) + total) % total;
  }

  private openNativeDialog(): void {
    if (!this.isBrowser) return;

    globalThis.setTimeout(() => {
      const dialog = this.popupDialog?.nativeElement;

      if (!dialog) {
        return;
      }

      if (!dialog.open) {
        dialog.showModal();
      }

      dialog.focus();
    });
  }

  private closeNativeDialog(): void {
    const dialog = this.popupDialog?.nativeElement;

    if (!dialog?.open) {
      return;
    }

    dialog.close();
  }

  private lockDocumentScroll(): void {
    if (!this.isBrowser || this.documentScrollLocked) {
      return;
    }

    const documentElement = document.documentElement;
    const body = document.body;
    const scrollbarWidth = window.innerWidth - documentElement.clientWidth;

    this.previousBodyOverflow = body.style.overflow;
    this.previousBodyPaddingRight = body.style.paddingRight;
    this.previousDocumentOverflow = documentElement.style.overflow;

    documentElement.style.overflow = 'hidden';
    body.style.overflow = 'hidden';

    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }

    this.documentScrollLocked = true;
  }

  private unlockDocumentScroll(): void {
    if (!this.isBrowser || !this.documentScrollLocked) {
      return;
    }

    document.documentElement.style.overflow = this.previousDocumentOverflow;
    document.body.style.overflow = this.previousBodyOverflow;
    document.body.style.paddingRight = this.previousBodyPaddingRight;
    this.documentScrollLocked = false;
  }

  private startAutoplay(): void {
    if (!this.canScheduleAutoplay()) return;
    if (this.autoplayTimer) return;

    this.autoplayTimer = globalThis.setTimeout(() => {
      this.autoplayTimer = null;

      if (!this.canScheduleAutoplay()) {
        return;
      }

      this.goNext('autoplay');
    }, this.resolveAutoplayInterval());
  }

  private restartAutoplay(): void {
    this.stopAutoplay();
    this.startAutoplay();
  }

  private stopAutoplay(): void {
    if (!this.autoplayTimer) return;

    globalThis.clearTimeout(this.autoplayTimer);
    this.autoplayTimer = null;
  }

  private resolveAutoplayInterval(): number {
    return Math.max(Number(this.autoplayIntervalMs) || 8000, 1500);
  }

  private canScheduleAutoplay(): boolean {
    return (
      this.isBrowser &&
      this.showPopup &&
      this.autoplay &&
      !this.autoplayPaused &&
      !this.isClosing &&
      !this.isSlideChanging &&
      this.hasMultipleFlyers
    );
  }

  private get autoplayPaused(): boolean {
    return this.autoplayHoverPaused || this.autoplayFocusPaused;
  }

  private waitForSlideTransition(): Promise<void> {
    return new Promise((resolve) => {
      this.slideTimeout = globalThis.setTimeout(() => {
        this.slideTimeout = null;
        resolve();
      }, this.slideTransitionMs);
    });
  }

  private preloadImage(imageUrl: string | null): Promise<void> {
    if (!this.isBrowser || !imageUrl || this.preloadedImageUrls.has(imageUrl)) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const image = new Image();
      let timeout: ReturnType<typeof globalThis.setTimeout> | null = null;

      const finish = (loaded: boolean): void => {
        if (timeout) {
          globalThis.clearTimeout(timeout);
          timeout = null;
        }

        image.onload = null;
        image.onerror = null;

        if (loaded) {
          this.preloadedImageUrls.add(imageUrl);
        }

        resolve();
      };

      timeout = globalThis.setTimeout(() => {
        finish(false);
      }, this.imagePreloadTimeoutMs);

      image.onload = () => {
        finish(true);
      };

      image.onerror = () => {
        finish(false);
      };

      image.src = imageUrl;
    });
  }

  private commitSlideChange(
    nextIndex: number,
    source: FlyerPopupSlideSource,
    requestId: number,
  ): void {
    if (requestId !== this.slideRequestId || !this.showPopup) {
      return;
    }

    this.activeIndex = nextIndex;
    this.isSlideChanging = false;
    this.syncImageState();
    this.emitSlideChange(source);
    this.restartAutoplay();
    this.cdr.markForCheck();
  }

  private clearCloseTimeout(): void {
    if (!this.closeTimeout) return;

    globalThis.clearTimeout(this.closeTimeout);
    this.closeTimeout = null;
  }

  private clearSlideTimeout(): void {
    if (!this.slideTimeout) return;

    globalThis.clearTimeout(this.slideTimeout);
    this.slideTimeout = null;
  }
}
